/**
 * Analysis period reporting windows: UTC storage, local-friendly display labels.
 * Wire format uses Unix epoch milliseconds (UTC instants).
 */

const DEFAULT_LOCALE = 'en-GB';

/**
 * First and last millisecond of a calendar month in UTC (inclusive end).
 */
export function utcMonthRangeInclusive(
	year: number,
	month1to12: number
): { startTimestamp: number; endTimestamp: number } {
	const m = month1to12 - 1;
	const startTimestamp = Date.UTC(year, m, 1, 0, 0, 0, 0);
	const endTimestamp = Date.UTC(year, m + 1, 0, 23, 59, 59, 999);
	return { startTimestamp, endTimestamp };
}

/** `YYYY-MM-DD` for the UTC calendar day of `ms`. */
export function utcYmdFromEpochMs(ms: number): string {
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return '';
	const y = d.getUTCFullYear();
	const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
	const day = String(d.getUTCDate()).padStart(2, '0');
	return `${y}-${mo}-${day}`;
}

/** Start of UTC calendar day for `YYYY-MM-DD`. */
export function utcStartOfDayFromYmd(ymd: string): number {
	const [y, mo, day] = ymd.split('-').map(Number);
	if (!y || !mo || !day) return Number.NaN;
	return Date.UTC(y, mo - 1, day, 0, 0, 0, 0);
}

/** Last millisecond of UTC calendar day for `YYYY-MM-DD` (inclusive end). */
export function utcEndOfDayInclusiveFromYmd(ymd: string): number {
	const [y, mo, day] = ymd.split('-').map(Number);
	if (!y || !mo || !day) return Number.NaN;
	return Date.UTC(y, mo - 1, day, 23, 59, 59, 999);
}

/**
 * Short range label using the viewer's local timezone for calendar grouping
 * (API timestamps are UTC instants).
 */
function formatAnalysisRangeShortLabel(
	startTimestamp: number,
	endTimestamp: number,
	locale = DEFAULT_LOCALE
): string {
	const start = new Date(startTimestamp);
	const end = new Date(endTimestamp);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

	const sameLocalMonth =
		start.getFullYear() === end.getFullYear()
		&& start.getMonth() === end.getMonth();

	if (sameLocalMonth) {
		return start.toLocaleDateString(locale, {
			month: 'short',
			year: 'numeric',
		});
	}

	const startPart = start.toLocaleDateString(locale, {
		day: 'numeric',
		month: 'short',
	});
	const endPart = end.toLocaleDateString(locale, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
	return `${startPart} – ${endPart}`;
}

/**
 * Drawer-style title: long month when start/end share the same local calendar month.
 */
/** True when the range is exactly one UTC calendar month (inclusive). */
export function isFullUtcCalendarMonth(
	startTimestamp: number,
	endTimestamp: number
): boolean {
	const startYm = utcReportingMonthFromEpochMs(startTimestamp);
	const endYm = utcReportingMonthFromEpochMs(endTimestamp);
	if (!startYm || startYm !== endYm) {
		return false;
	}
	const [y, m] = startYm.split('-').map(Number);
	const month = utcMonthRangeInclusive(y, m);
	return (
		startTimestamp === month.startTimestamp
		&& endTimestamp === month.endTimestamp
	);
}

/**
 * Display title for an analysis period — "April 2026" for a full UTC month, else a short range.
 */
export function formatAnalysisTitle(
	startTimestamp: number,
	endTimestamp: number,
	locale = DEFAULT_LOCALE
): string {
	if (isFullUtcCalendarMonth(startTimestamp, endTimestamp)) {
		const ym = utcReportingMonthFromEpochMs(startTimestamp);
		if (ym) {
			const [y, m] = ym.split('-').map(Number);
			return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(locale, {
				month: 'long',
				year: 'numeric',
				timeZone: 'UTC',
			});
		}
	}
	return formatAnalysisRangeShortLabel(startTimestamp, endTimestamp, locale);
}

export function formatAnalysisRangeLongMonthYear(
	startTimestamp: number,
	endTimestamp: number,
	locale = DEFAULT_LOCALE
): string {
	const start = new Date(startTimestamp);
	const end = new Date(endTimestamp);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';

	const sameLocalMonth =
		start.getFullYear() === end.getFullYear()
		&& start.getMonth() === end.getMonth();

	if (sameLocalMonth) {
		return start.toLocaleDateString(locale, {
			month: 'long',
			year: 'numeric',
		});
	}

	return formatAnalysisRangeShortLabel(startTimestamp, endTimestamp, locale);
}

/** UTC calendar month `YYYY-MM` from epoch ms (uses UTC month of the instant). */
function utcReportingMonthFromEpochMs(ms: number): string | null {
	const d = new Date(ms);
	if (Number.isNaN(d.getTime())) return null;
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, '0');
	return `${y}-${m}`;
}

/** Inclusive UTC calendar-day span as `YYYY-MM-DD` for AgentCore analysis report. */
export function reportingDateSpanFromEpoch(
	startTimestamp: number,
	endTimestamp: number
): { startDate: string; endDate: string } | null {
	const startDate = utcYmdFromEpochMs(startTimestamp);
	const endDate = utcYmdFromEpochMs(endTimestamp);
	if (!startDate || !endDate) return null;
	if (startDate > endDate) return null;
	return { startDate, endDate };
}

/** True when the inclusive UTC day span is valid and does not extend past today (UTC). */
export function isReportingRangeOnOrBeforeToday(
	startTimestamp: number,
	endTimestamp: number
): boolean {
	const dates = reportingDateSpanFromEpoch(startTimestamp, endTimestamp);
	if (!dates) return false;
	const todayYmd = utcYmdFromEpochMs(Date.now());
	if (!todayYmd) return false;
	return dates.startDate <= todayYmd && dates.endDate <= todayYmd;
}
